import selectableColumnType from "@harbor/elements/utils/table/columnTypes/selectable";
import { defaultTextValue } from "../utils/enums";
import i18next from 'i18next';

// Helper function to get translations using global i18next from views namespace
export const tViews = (key, paramsOrDefault = '') => {
  try {
    // If second argument is a string, treat it as defaultValue (backward compatibility)
    // If it's an object, treat it as interpolation parameters
    const isParamsObject = typeof paramsOrDefault === 'object' && paramsOrDefault !== null && !Array.isArray(paramsOrDefault);

    if (isParamsObject) {
      // Pass interpolation parameters directly to i18next
      return i18next.isInitialized ? i18next.t(key, { ns: 'views', ...paramsOrDefault }) : '';
    } else {
      // Backward compatibility: string defaultValue
      return i18next.isInitialized ? i18next.t(key, { ns: 'views', defaultValue: paramsOrDefault }) : paramsOrDefault;
    }
  } catch (error) {
    console.warn(`Translation key "${key}" not found in views namespace`);
    return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
  }
};

export const tConfig = (key, paramsOrDefault = '') => {
  try {
    const isParamsObject = typeof paramsOrDefault === 'object' && paramsOrDefault !== null && !Array.isArray(paramsOrDefault);
    if (isParamsObject) {
      return i18next.isInitialized ? i18next.t(key, { ns: 'config', ...paramsOrDefault }) : '';
    } else {
      return i18next.isInitialized ? i18next.t(key, { ns: 'config', defaultValue: paramsOrDefault }) : paramsOrDefault;
    }
  } catch (error) {
    console.warn(`Translation key "${key}" not found in config namespace`);
    return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
  }
};

export const tUtils = (key, paramsOrDefault = '') => {
  try {
    const isParamsObject = typeof paramsOrDefault === 'object' && paramsOrDefault !== null && !Array.isArray(paramsOrDefault);
    if (isParamsObject) {
      return i18next.isInitialized ? i18next.t(key, { ns: 'utils', ...paramsOrDefault }) : '';
    } else {
      return i18next.isInitialized ? i18next.t(key, { ns: 'utils', defaultValue: paramsOrDefault }) : paramsOrDefault;
    }
  } catch (error) {
    console.warn(`Translation key "${key}" not found in utils namespace`);
    return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
  }
};

export const tCommon = (key, paramsOrDefault = '') => {
  try {
    const isParamsObject = typeof paramsOrDefault === 'object' && paramsOrDefault !== null && !Array.isArray(paramsOrDefault);
    if (isParamsObject) {
      return i18next.isInitialized ? i18next.t(key, { ns: 'common', ...paramsOrDefault }) : '';
    } else {
      return i18next.isInitialized ? i18next.t(key, { ns: 'common', defaultValue: paramsOrDefault }) : paramsOrDefault;
    }
  } catch (error) {
    console.warn(`Translation key "${key}" not found in common namespace`);
    return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
  }
};

export const  tGenerics = (key, paramsOrDefault = '') => {
  try {
    const isParamsObject = typeof paramsOrDefault === 'object' && paramsOrDefault !== null && !Array.isArray(paramsOrDefault);
    if (isParamsObject) {
      return i18next.isInitialized ? i18next.t(key, { ns: 'generics', ...paramsOrDefault }) : '';
    } else {
      return i18next.isInitialized ? i18next.t(key, { ns: 'generics', defaultValue: paramsOrDefault }) : paramsOrDefault;
    }
  } catch (error) {
    console.warn(`Translation key "${key}" not found in generics namespace`);
    return typeof paramsOrDefault === 'string' ? paramsOrDefault : '';
  }
};

/**
 * This callback type is called on the select event
 *
 * @callback selectCallback
 * @param {[string]} selections is the selected rows's ids
 * @param {[object]} source is the table data
 * @returns {undefined}
 */
/**
 * Description: get the selectable column object
 * @param {object} store is the table store
 * @param {selectCallback} selectCallback is called on the select event
 * @returns {object} - is the column object
 */

export const getSelectableColumn = (store, selectCallback = () => null, ariaLabel="") => {
  const selectableRowsHook = store.getHook("selectableRows");
  return {
    id: "selectable_col",
    ...selectableColumnType({ store }),
    columnTemplate: createElement => {
      const { source, selected, query: { maxSelections } } = store.getState();
      const rowCount = source.length;
      const maxRowSelections =
        rowCount > 0 && rowCount < maxSelections ? rowCount : maxSelections;
      return [createElement("hbr-checkbox", {
        indeterminate: selected.length > 0 && selected.length < maxRowSelections,
        checked: selected.length === maxRowSelections,
        disabled: rowCount === 0,
        style: { margin: "var(--cell-padding)" },
        "aria-label": `${ariaLabel}`,
        "data-testid": "checkbox-header",
        "onHbr-click": event => {
          event.target.blur();
          const checked = event.target.checked;
          const selections =
            checked === true ? source.slice(0, 5).map(item => item.id) : [];
          selectableRowsHook.setSelected(selections);
          selectCallback(selections, source);
        }
      })];
    },
    cellTemplate: (createElement, props) => {
      const uniqueId = props.model.id;
      const { source, selected, query: { maxSelections } } = store.getState();
      return createElement("hbr-checkbox", {
        checked: selected.includes(uniqueId) ? true : undefined,
        "data-rowIndex": props.rowIndex,
        "data-testid": uniqueId,
        "aria-label": `${source[props.rowIndex].application}`,
        intermediate: false,
        disabled: selected.length >= maxSelections && !selected.includes(uniqueId),
        "onHbr-click": event => {
          event.target.blur();
          const { selected: current } = store.getState();
          const selections = [...current];
          const selectionIndex = selections.indexOf(uniqueId);
          if (selectionIndex === -1) selections.push(uniqueId);
          else selections.splice(selectionIndex, 1);
          selectableRowsHook.setSelected(selections);
          selectCallback(selections, source);
        }
      });
    }
  }
};


/**
 * Description: get the selectable column object
 * @param {object} store is the table store
 * @param {selectCallback} selectCallback is called on the select event
 * @returns {object} - is the column object
 */
export const getSelectableColumnActionTable = (store, selectCallback = () => null) => {
  const selectableRowsHook = store.getHook("selectableRows");
  return {
    id: "selectable_col",
    ...selectableColumnType({ store }),
    columnTemplate: createElement => {
      const { source, selected, query: { maxSelections } } = store.getState();
      const rowCount = source.length;
      const maxRowSelections =
        rowCount > 0 && rowCount < maxSelections ? rowCount : maxSelections;
      return [createElement("hbr-checkbox", {
        indeterminate: selected.length > 0 && selected.length < maxRowSelections,
        checked: selected.length === maxRowSelections,
        disabled: rowCount === 0,
        style: { margin: "var(--cell-padding)" },
        "data-testid": "checkbox-header",
        "onHbr-click": event => {
          event.target.blur();
          const checked = event.target.checked;
          const selections =
            checked === true ? source.map(item => item.id) : [];
          selectableRowsHook.setSelected(selections);
          selectCallback(selections, source);
        }
      })];
    },
    cellTemplate: (createElement, props) => {
      const uniqueId = props.model.id;
      const { source, selected, query: { maxSelections } } = store.getState();
      return createElement("hbr-checkbox", {
        checked: selected.includes(uniqueId) ? true : undefined,
        "data-rowIndex": props.rowIndex,
        "data-testid": uniqueId,
        intermediate: false,
        disabled: selected.length >= maxSelections && !selected.includes(uniqueId),
        "onHbr-click": event => {
          event.target.blur();
          const { selected: current } = store.getState();
          const selections = [...current];
          const selectionIndex = selections.indexOf(uniqueId);
          if (selectionIndex === -1) selections.push(uniqueId);
          else selections.splice(selectionIndex, 1);
          selectableRowsHook.setSelected(selections);
          selectCallback(selections, source);
        }
      });
    }
  }
};
/**
 * Description: Returns a selectable column cell as a radio button for single row selection.
 * @param {object} store - The table store.
 * @param {selectCallback} selectCallback - Called on the select event.
 * @param {Function} createElement - Harbor's createElement function.
 * @param {object} props - Cell props, including model and rowIndex.
 * @returns {HTMLElement} - The radio button element.
 */
export const getSelectableColumnAsRadioBtn = (store, selectCallback, createElement, props) => {
  const selectableRowsHook = store.getHook("selectableRows");
  const { selected } = store.getState();
  const selectedRowIndex = Array.isArray(selected) && selected.length > 0 ? selected[0] : 0;
  const isChecked = props.rowIndex === selectedRowIndex;

  return createElement("hbr-radio", {
    value: props.model.circuit_display_name,
    checked: isChecked,
    name: "circuit-selection",
    "aria-label": `Select circuit ${props.model.circuit_display_name}`,
    "data-row-index": props.rowIndex,
    "data-testid": `radio-${props.rowIndex}`,
    intermediate: false,
    'onHbr-click': (event) => {
      event.target.blur();
      selectableRowsHook.setSelected([props.rowIndex]);
      selectCallback({ circuit: props.model }); // Store selected circuit in Redux for bandwidth API
    }
  });
};

/**
 * Description: get table column header template with info icon and tooltip
 * @param {string} id - unique identifier for the info button and tooltip anchor
 * @param {string} msg - the tooltip text content to display
 * @param {Function} h - Harbor's createElement function for creating virtual DOM elements
 * @returns {HTMLElement} - the info button element with embedded tooltip portal
 */
export const getColumnTemplateWithInfo = (id, msg, h) => {
  const content = h("hbr-icon-button", {
    id: id,
    slot: "suffix",
    style: { fontSize: "14px", "--padding": "3px" },
    name: "info",
     innerHTML: `
               <hbr-portal>
          <hbr-tooltip anchor="#${id}">
            <div slot="content">
              ${msg}
            </div>
          </hbr-tooltip>
        </hbr-portal>
    `
  });
  return content;
};

/**
 * This callback type is called on the click event of the link.
 *
 * @callback linkClickCallback
 * @param {Function} navigate is the React Router navigate function
 * @param {object} model is the row data
 * @returns {undefined}
 */
/**
 * Description: get table data cell template with link
 * @param {Function} navigate is the React Router navigate function
 * @param {linkClickCallback} onClick is called on the click event of the link
 * @param {Function} createElement is an internal function of harbor to create elements
 * @param {object} data - this has row data (model) and the column property name (prop)
 * @returns {HTMLElement} - is the element returned by the createElement function
 */
export const getLinkCellTemplate = (
  navigate,
  onClick,
  createElement,
  { model, prop }
) => {
  const linkElement = createElement(
    "div",
    {
      class: "table-link",
      onClick: () => onClick(navigate, model, prop)
    },
    model[prop]
  );
  return createElement("div", { class: "flex-items" }, linkElement);
};

/**
 * Description: get table data cell template with toolip
 * @param {string} tableId is the table id (unique)
 * @param {Function} createElement is an internal function of harbor to create elements
 * @param {object} data - this has row data (model) and the column property name (prop)
 * @returns {HTMLElement} - is the element returned by the createElement function
 */
export const getCellTemplateWithTooltip = (
  tableId,
  createElement,
  { model, prop }
) => {
  const id = model.id ? `${tableId}_${model.id}_${prop}` : `${tableId}_${prop[model]}_${Math.random().toString().replace(".", "_")}`;
  const text = (model[prop] === null || model[prop] === undefined) ? defaultTextValue : model[prop];

  return createElement("div", {
    class: "flex-items",
    innerHTML: `
      <div id="${id}" class="overflow-ellipsis">${text}</div>
      <hbr-portal>
        <hbr-tooltip anchor="#${id}">
          <div slot="content">${text}</div>
        </hbr-tooltip>
      </hbr-portal>
    `
  });
};

/**
 * Description: get table cell template with link and toolip
 * @param {Function} navigate is the React Router navigate function
 * @param {linkClickCallback} onClick is called on the click event of the link
 * @param {Function} createElement is an internal function of harbor to create elements
 * @param {object} data - this has row data (model) and the column property name (prop)
 * @param {string} tableId is the table id (unique)
 * @param {Function} createElement is an internal function of harbor to create elements
 * @returns {HTMLElement} - is the element returned by the createElement function
 */
export const getCellTemplateWithTooltipAndLink = (
  navigate,
  onClick,
  createElement,
  { model, prop },
  tableId
) => {
  const id = model.id ? `${tableId}_${model.id}_${prop}` : `${tableId}_${prop[model]}_${Math.random().toString().replace(".", "_")}`;
  const text = (model[prop] === null || model[prop] === undefined) ? defaultTextValue : model[prop];

  return createElement("div", {
    class: "flex-items",
    onClick: () => onClick(navigate, model, prop),
    innerHTML: `
      <div id="${id}" class="table-link overflow-ellipsis">${text}</div>
      <hbr-portal>
        <hbr-tooltip anchor="#${id}">
          <div slot="content">${text}</div>
        </hbr-tooltip>
      </hbr-portal>
    `
  });
};

export const setToggledColumns = (
  column,
  localStorageTglListName,
  defaultToggleList
) => {
  if (localStorageTglListName) {
    let existingToggledColumns = localStorage.getItem(localStorageTglListName);
    let updatedToggledList = existingToggledColumns ? JSON.parse(existingToggledColumns) : [];
    let updatedToggledColumnList = updatedToggledList.columnsHidden ? updatedToggledList.columnsHidden : [...defaultToggleList];
    if (column.checked) {
      let indexOfToggled = updatedToggledColumnList.indexOf(column.value);
      updatedToggledColumnList.splice(indexOfToggled, 1);
    } else {
      updatedToggledColumnList.push(column.value);
    }
    let updateList = {};
    updateList.columnsHidden = updatedToggledColumnList;
    if (updatedToggledList.tableDensity) {
      updateList.tableDensity = updatedToggledList.tableDensity;
    }
    localStorage.setItem(localStorageTglListName, JSON.stringify(updateList));
  }
};

export const useTableSettings = (
  localStorageLabel,
  defaultTglColumns,
  columnsHook,
  tableStore
) => {
  let existingTableSettings = localStorage.getItem(localStorageLabel);
  let hideDefault = [...defaultTglColumns];
  if (existingTableSettings) {
    existingTableSettings = JSON.parse(existingTableSettings);
    let existingToggledColumns = existingTableSettings?.columnsHidden;
    let tablesize = existingTableSettings?.tableDensity ? existingTableSettings?.tableDensity : "compact";
    tableStore.setState({ size: tablesize });
    if (existingToggledColumns) {
      existingToggledColumns?.forEach(element => {
        columnsHook.toggle(element, false);
      });
    } else {
      hideDefault?.forEach(element => {
        columnsHook.toggle(element, false);
      });
    }
  } else {
    hideDefault?.forEach(element => {
      tableStore.setState({ size: "compact" });
      columnsHook.toggle(element, false);
    });
  }
}

export const setTableDensity = (
  size,
  localStorageTglListName
) => {
  if (localStorageTglListName) {
    let existingToggledColumns = localStorage.getItem(localStorageTglListName);
    let updatedToggledList = existingToggledColumns ? JSON.parse(existingToggledColumns) : [];
    let updatedToggledColumnList = size;
    let updateList = {};
    updateList.tableDensity = updatedToggledColumnList;
    if (updatedToggledList.columnsHidden) {
      updateList.columnsHidden = updatedToggledList.columnsHidden;
    }
    localStorage.setItem(localStorageTglListName, JSON.stringify(updateList));
  }
};
export const hiddenColumns = {
  application_dashboard_table_main: {
    ids: ["application"],
  },
  generated_reports_table: {
    ids: ["report_name"],
  },
  predictive_nw_recommend_table_main: {
    ids: ["appClass"],
  },
  predictive_nw_device_pair_table: {
    ids: ["localSite","action"],
  },
  ad_usage_applications_table: {
    ids: ["application_col"],
  },
  ad_usage_anomaly_summary_table: {
    ids: ["display_circuit_name_col","site_name_col"],
  },
  ad_sites_table: {
    ids: ["site_name_col"],
  },
  ad_single_site_table: {
    ids: ["tunnel_name_col"],
  },
  ad_usage_circuits_availability_table: {
    ids: ["display_circuit_name_col"],
  },
  ad_usage_top_clients_table: {
    ids: ["source_ip_col"],
  },
   sites_table:{
    ids: ["site_name"]
  },
  app360_clients_table:{
    ids: ["site_name_col"]
  }
};
